import React, { useState, useMemo } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getUsers, createUser, deactivateUser, updateUser } from '../api/user';
import { getCompanies } from '../api/company';
import { User, UserRole } from '../types/Auth';
import { useAuth } from '../hooks/useAuth';
import Loader from '../components/Loader';
import EmptyState from '../components/EmptyState';

const allRoles: UserRole[] = ['super-admin', 'admin', 'incidence-manager', 'it-member'];
const adminAssignableRoles: UserRole[] = ['incidence-manager', 'it-member'];

const UserManagement: React.FC = () => {
    const queryClient = useQueryClient();
    const { state: authState } = useAuth();
    const currentUser = authState.user;

    // State for Modals
    const [isCreateModalOpen, setCreateModalOpen] = useState(false);
    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [isDeactivateModalOpen, setDeactivateModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    
    // State for Forms
    const [newUserName, setNewUserName] = useState('');
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserPassword, setNewUserPassword] = useState('');
    const [newUserPasswordConfirm, setNewUserPasswordConfirm] = useState('');
    const [newUserPasswordVisible, setNewUserPasswordVisible] = useState(false);
    const [newUserPasswordConfirmVisible, setNewUserPasswordConfirmVisible] = useState(false);
    const [newUserRole, setNewUserRole] = useState<UserRole>('it-member');
    const [newUserItRole, setNewUserItRole] = useState<string>('');
    const [newUserCompanyId, setNewUserCompanyId] = useState<string>('');
    // Edit form state
    const [editName, setEditName] = useState('');
    const [editEmail, setEditEmail] = useState('');
    const [editRole, setEditRole] = useState<UserRole>('it-member');
    const [editCompanyId, setEditCompanyId] = useState<string>('');
    const [editWelcomeSent, setEditWelcomeSent] = useState<boolean>(false);
    const [editAcceptedAt, setEditAcceptedAt] = useState<string>('');
    const [editPassword, setEditPassword] = useState<string>('');
    const [editPasswordConfirm, setEditPasswordConfirm] = useState<string>('');
    const [editPasswordVisible, setEditPasswordVisible] = useState(false);
    const [editPasswordConfirmVisible, setEditPasswordConfirmVisible] = useState(false);
    const [editItRole, setEditItRole] = useState<string>('');
    // Password rules
    const PASSWORD_MIN_LENGTH = 8;

    const getPasswordStrength = (pw: string) => {
        let score = 0;
        if (!pw) return { score: 0, label: 'empty' };
        if (pw.length >= PASSWORD_MIN_LENGTH) score += 1;
        if (/[0-9]/.test(pw)) score += 1;
        if (/[A-Z]/.test(pw)) score += 1;
        if (/[^A-Za-z0-9]/.test(pw)) score += 1;
        const label = score <= 1 ? 'Weak' : score === 2 ? 'Medium' : 'Strong';
        return { score, label };
    };

    const passwordStrength = getPasswordStrength(editPassword);
    const isPasswordValid = editPassword === '' || editPassword.length >= PASSWORD_MIN_LENGTH;
    const doPasswordsMatch = editPassword === editPasswordConfirm;
    // Create modal password derived values
    const newUserPasswordStrength = getPasswordStrength(newUserPassword);
    const isNewUserPasswordValid = newUserPassword.length >= PASSWORD_MIN_LENGTH;
    const doNewUserPasswordsMatch = newUserPassword === newUserPasswordConfirm;

    const getStrengthIcon = (score: number) => score <= 1 ? '🔴' : score === 2 ? '🟡' : '🟢';

    // Data Fetching
    const { data: users, isLoading: isLoadingUsers } = useQuery<User[]>({ queryKey: ['users'], queryFn: getUsers });
    const { data: companies } = useQuery({ queryKey: ['companies'], queryFn: getCompanies, enabled: currentUser?.roles.includes('super-admin') });

    // Mutations
    const createUserMutation = useMutation({ mutationFn: createUser, onSuccess: () => { queryClient.invalidateQueries({queryKey: ['users']}); setCreateModalOpen(false); toast.success('User created!'); } });
    
    const deactivateUserMutation = useMutation({ mutationFn: deactivateUser, onSuccess: () => { queryClient.invalidateQueries({queryKey: ['users']}); setDeactivateModalOpen(false); toast.success('User deactivated!'); } });
    const updateUserMutation = useMutation({ mutationFn: updateUser, onSuccess: () => { queryClient.invalidateQueries({queryKey: ['users']}); setEditModalOpen(false); toast.success('User updated!'); } });

    const filteredUsers = useMemo(() => {
        if (!users || !currentUser) return [];
        const safeUsers = users.map(user => ({ ...user, roles: user.roles || [] }));
        if (currentUser.roles.includes('super-admin')) return safeUsers;
        if (currentUser.roles.includes('admin') && currentUser.company_id) return safeUsers.filter(user => user.company_id === currentUser.company_id);
        return [];
    }, [users, currentUser]);

    const availableRolesToAssign = currentUser?.roles.includes('super-admin') ? allRoles : adminAssignableRoles;

    const handleCreateUser = (e: React.FormEvent) => {
        e.preventDefault();
        const payload: any = { name: newUserName, email: newUserEmail, password: newUserPassword, roles: [newUserRole] };
        if (newUserRole !== 'super-admin') {
            payload.company_id = newUserCompanyId;
        }
        if (newUserRole === 'it-member' && newUserItRole) {
            payload.it_role = newUserItRole;
        }
        createUserMutation.mutate(payload);
    };

    // role-only update is no longer used (we now use the full edit flow)

    const handleUpdateUser = (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!selectedUser) return;
        const payload: any = {
            name: editName,
            email: editEmail,
            roles: [editRole],
            welcome_sent: !!editWelcomeSent,
        };
        if (editPassword) payload.password = editPassword;
        if (editRole === 'it-member' && editItRole) {
            payload.it_role = editItRole;
        }
        if (editCompanyId) payload.company_id = editCompanyId;
        if (editAcceptedAt) {
            try {
                payload.accepted_at = new Date(editAcceptedAt).toISOString();
            } catch (err) {
                // ignore invalid date
            }
        }
        updateUserMutation.mutate({ id: selectedUser.id, data: payload });
    };

    const openEditModal = (user: User) => {
        setSelectedUser(user);
        setEditName(user.name || '');
        setEditEmail(user.email || '');
        setEditRole(user.roles[0] || 'it-member');
        setEditCompanyId(user.company_id || '');
        setEditWelcomeSent(!!user.welcome_sent);
        setEditAcceptedAt(user.accepted_at ? new Date(user.accepted_at).toISOString().slice(0, 10) : '');
        setEditPassword('');
        setEditItRole((user as any).it_role || '');
        setEditModalOpen(true);
    };

    const openDeactivateModal = (user: User) => {
        setSelectedUser(user);
        setDeactivateModalOpen(true);
    };

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">User Management</h1>
                {currentUser?.roles.includes('super-admin') && (
                    <button onClick={() => setCreateModalOpen(true)} className="px-4 py-2 bg-indigo-600 text-white font-semibold rounded-lg shadow-md" data-cy="create-user-button">Create User</button>
                )}
            </div>

            <div className="bg-white rounded-lg shadow-md">
                 <h2 className="text-xl font-bold p-4 border-b">All Users</h2>
                {isLoadingUsers ? <div className="p-4"><Loader /></div> : (
                    <ul className="divide-y divide-gray-200">
                        {filteredUsers.map(user => (
                            <li key={user.id} className="p-4 flex justify-between items-center">
                                <div>
                                    <p className="font-medium">{user.name || 'N/A'}</p>
                                    <p className="text-sm text-gray-500">{user.email}</p>
                                    <p className="text-sm text-gray-500">Company: {companies?.find(c => c.id === user.company_id)?.name || user.company_id || 'N/A'}</p>
                                    <p className="text-xs text-gray-400">Roles: {(user.roles || []).join(', ')}</p>
                                    <div className="mt-2 flex items-center space-x-2">
                                        <span className={`px-2 py-0.5 text-xs rounded ${user.welcome_sent ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                                            {user.welcome_sent ? 'Welcome Sent' : 'Welcome Not Sent'}
                                        </span>
                                        <span className={`px-2 py-0.5 text-xs rounded ${user.accepted_at ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                            {user.accepted_at ? `Accepted ${new Date(user.accepted_at).toLocaleDateString()}` : 'Pending'}
                                        </span>
                                    </div>
                                </div>
                                <div className="space-x-2">
                                    <button onClick={() => openEditModal(user)} className="px-3 py-1 text-sm text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200">Edit</button>
                                    <button onClick={() => openDeactivateModal(user)} className="px-3 py-1 text-sm text-red-600 bg-red-100 rounded-md hover:bg-red-200">Deactivate</button>
                                </div>
                            </li>
                        ))}
                        {filteredUsers.length === 0 && <EmptyState title="No Users Found" message="There are no users matching your criteria." />}
                    </ul>
                )}
            </div>

            {isCreateModalOpen && (
                 <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
                        <h2 className="text-xl font-bold mb-4">Create New User</h2>
                        <form onSubmit={handleCreateUser} className="space-y-4">
                            <input data-cy="new-user-name" type="text" value={newUserName} onChange={(e) => setNewUserName(e.target.value)} placeholder="Name" className="mt-1 block w-full p-2 border border-gray-300 rounded-md" required />
                            <input data-cy="new-user-email" type="email" value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} placeholder="Email" className="mt-1 block w-full p-2 border border-gray-300 rounded-md" required />
                            <div className="mt-1 relative">
                                <div className="flex items-center">
                                    <input data-cy="new-user-password" type={newUserPasswordVisible ? 'text' : 'password'} value={newUserPassword} onChange={(e) => setNewUserPassword(e.target.value)} placeholder="Password" className="block w-full p-2 border border-gray-300 rounded-md" required />
                                    <button type="button" onClick={() => setNewUserPasswordVisible(v => !v)} className="ml-2 p-1 text-gray-600" aria-label="Toggle password visibility">
                                        {newUserPasswordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>
                            <div className="mt-2">
                                <div className="h-2 w-full bg-gray-200 rounded overflow-hidden">
                                    <div style={{ width: `${(newUserPasswordStrength.score / 4) * 100}%` }} className={`h-2 ${newUserPasswordStrength.score <= 1 ? 'bg-red-500' : newUserPasswordStrength.score === 2 ? 'bg-yellow-400' : 'bg-green-500'}`}></div>
                                </div>
                                <p className="text-xs mt-1 text-gray-600">Strength: <span className="font-medium">{getStrengthIcon(newUserPasswordStrength.score)} {newUserPasswordStrength.label}</span></p>
                                {!isNewUserPasswordValid && newUserPassword !== '' && <p className="text-xs text-red-600 mt-1">Password must be at least {PASSWORD_MIN_LENGTH} characters.</p>}
                                <div className="mt-2 relative">
                                    <div className="flex items-center">
                                        <input data-cy="new-user-password-confirm" type={newUserPasswordConfirmVisible ? 'text' : 'password'} value={newUserPasswordConfirm} onChange={(e) => setNewUserPasswordConfirm(e.target.value)} placeholder="Confirm password" className="block w-full p-2 border border-gray-300 rounded-md" required />
                                        <button type="button" onClick={() => setNewUserPasswordConfirmVisible(v => !v)} className="ml-2 p-1 text-gray-600" aria-label="Toggle confirm password visibility">
                                            {newUserPasswordConfirmVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                </div>
                                {newUserPassword !== '' && !doNewUserPasswordsMatch && <p className="text-xs text-red-600 mt-1">Passwords do not match.</p>}
                            </div>
                            <select data-cy="new-user-role-select" value={newUserRole} onChange={(e) => setNewUserRole(e.target.value as UserRole)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md">
                                {availableRolesToAssign.map(role => <option key={role} value={role}>{role}</option>)}
                            </select>
                            {newUserRole === 'it-member' && (
                                <input type="text" value={newUserItRole} onChange={(e) => setNewUserItRole(e.target.value)} placeholder="IT role (e.g. oncall, infra)" className="mt-2 block w-full p-2 border border-gray-300 rounded-md" />
                            )}
                            {newUserRole !== 'super-admin' && (
                                <select data-cy="new-user-company-select" value={newUserCompanyId} onChange={(e) => setNewUserCompanyId(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" required>
                                    <option value="" disabled>Select a company...</option>
                                    {companies?.map(company => <option key={company.id} value={company.id}>{company.name}</option>)}
                                </select>
                            )}
                            <div className="flex justify-end space-x-2 pt-4">
                                <button type="button" onClick={() => setCreateModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded-md">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-md" disabled={createUserMutation.isPending || !isNewUserPasswordValid || !doNewUserPasswordsMatch}>Create</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {isEditModalOpen && selectedUser && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
                        <h2 className="text-xl font-bold mb-4">Edit User {selectedUser.email}</h2>
                        <form onSubmit={handleUpdateUser} className="space-y-4">
                            <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Name" className="mt-1 block w-full p-2 border border-gray-300 rounded-md" required />
                            <input type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} placeholder="Email" className="mt-1 block w-full p-2 border border-gray-300 rounded-md" required />
                            <div className="mt-1 relative">
                                <div className="flex items-center">
                                    <input type={editPasswordVisible ? 'text' : 'password'} value={editPassword} onChange={(e) => setEditPassword(e.target.value)} placeholder="Password (leave blank to keep current)" className="block w-full p-2 border border-gray-300 rounded-md" />
                                    <button type="button" onClick={() => setEditPasswordVisible(v => !v)} className="ml-2 p-1 text-gray-600" aria-label="Toggle password visibility">
                                        {editPasswordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>
                            <div className="mt-2">
                                <div className="h-2 w-full bg-gray-200 rounded overflow-hidden">
                                    <div style={{ width: `${(passwordStrength.score / 4) * 100}%` }} className={`h-2 ${passwordStrength.score <= 1 ? 'bg-red-500' : passwordStrength.score === 2 ? 'bg-yellow-400' : 'bg-green-500'}`}></div>
                                </div>
                                <p className="text-xs mt-1 text-gray-600">Strength: <span className="font-medium">{passwordStrength.label}</span></p>
                                {!isPasswordValid && <p className="text-xs text-red-600 mt-1">Password must be at least {PASSWORD_MIN_LENGTH} characters.</p>}
                                <div className="mt-2 relative">
                                    <div className="flex items-center">
                                        <input type={editPasswordConfirmVisible ? 'text' : 'password'} value={editPasswordConfirm} onChange={(e) => setEditPasswordConfirm(e.target.value)} placeholder="Confirm password" className="block w-full p-2 border border-gray-300 rounded-md" />
                                        <button type="button" onClick={() => setEditPasswordConfirmVisible(v => !v)} className="ml-2 p-1 text-gray-600" aria-label="Toggle confirm password visibility">
                                            {editPasswordConfirmVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                </div>
                                {editPassword && !doPasswordsMatch && <p className="text-xs text-red-600 mt-1">Passwords do not match.</p>}
                            </div>
                            <select value={editRole} onChange={(e) => setEditRole(e.target.value as UserRole)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md">
                                {availableRolesToAssign.map(role => <option key={role} value={role}>{role}</option>)}
                            </select>
                            {editRole === 'it-member' && (
                                <input type="text" value={editItRole} onChange={(e) => setEditItRole(e.target.value)} placeholder="IT role (e.g. oncall, infra)" className="mt-2 block w-full p-2 border border-gray-300 rounded-md" />
                            )}
                            {editRole !== 'super-admin' && (
                                <select value={editCompanyId} onChange={(e) => setEditCompanyId(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md">
                                    <option value="">Select a company...</option>
                                    {companies?.map(company => <option key={company.id} value={company.id}>{company.name}</option>)}
                                </select>
                            )}
                            <div className="flex items-center space-x-4">
                                <label className="flex items-center space-x-2">
                                    <input type="checkbox" checked={editWelcomeSent} onChange={(e) => setEditWelcomeSent(e.target.checked)} />
                                    <span className="text-sm">Welcome Sent</span>
                                </label>
                                <label className="flex items-center space-x-2">
                                    <span className="text-sm">Accepted At</span>
                                    <input type="date" value={editAcceptedAt} onChange={(e) => setEditAcceptedAt(e.target.value)} className="mt-1 block p-1 border border-gray-300 rounded-md" />
                                </label>
                            </div>
                            <div className="flex justify-end space-x-2 pt-2">
                                <button type="button" onClick={() => setEditModalOpen(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md" disabled={updateUserMutation.isPending || !isPasswordValid || !doPasswordsMatch}>Save</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {isDeactivateModalOpen && selectedUser && (
                 <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
                        <h2 className="text-xl font-bold mb-4">Deactivate User</h2>
                        <p>Are you sure you want to deactivate the user "<span className="font-bold">{selectedUser.email}</span>"?</p>
                        <div className="flex justify-end space-x-2 mt-6">
                            <button type="button" onClick={() => setDeactivateModalOpen(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md">Cancel</button>
                            <button onClick={() => deactivateUserMutation.mutate(selectedUser.id)} className="px-4 py-2 bg-red-600 text-white rounded-md" disabled={deactivateUserMutation.isPending}>Deactivate</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;
